import { useEffect, useRef, useState } from "react";
import { Modal } from "@/ui/Modal";
import { Badge } from "@/ui/Badge";
import { Calout } from "@/ui/Calout";
import { checkHtmlAddresses, updatePage, makeSlug } from "@/api/pages";
import { getSessionToken } from "@/api/auth";

// AMB-700 — rename an HTML page and/or change its address. The address is checked as you
// type. For a published page, the next publish puts it at the new address and takes the
// old one offline (the server sends the old address along when publishing).
export const RenameHtmlPageModal = ({
  page,
  campaignId,
  siteAddress,
  setIsOpen,
  onSaved,
}: {
  page: { id: string; name?: string; slug?: string; pageSettings?: any };
  campaignId: string;
  siteAddress?: string;
  setIsOpen: (open: boolean) => void;
  onSaved: () => void;
}) => {
  const token = getSessionToken() || "";
  const [name, setName] = useState(page?.name || "");
  const [slug, setSlug] = useState(page?.slug || "");
  const [check, setCheck] = useState<{ level?: string; reason?: string }>({ level: "ok", reason: "Current address" });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const timer = useRef<any>(null);

  const liveSlug = page?.pageSettings?.html?.publishedSlug;
  const cleanSlug = makeSlug(slug || "");
  const changed = cleanSlug !== page?.slug;

  useEffect(() => {
    if (!changed) return setCheck({ level: "ok", reason: "Current address" });
    setCheck({});
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      const out = await checkHtmlAddresses({ token, campaignId, requests: [{ key: "a", slug: cleanSlug, pageId: page.id }] });
      const r = out?.ok ? (out.results || [])[0] : null;
      setCheck(r ? { level: r.level, reason: r.reason } : { level: "blocked", reason: out?.error || "Couldn't check this address." });
    }, 350);
  }, [cleanSlug]);

  const canSave = Boolean(name.trim() && cleanSlug && (!changed || check.level === "ok"));

  const handleSave = async () => {
    if (saving || !canSave) return setError(!name.trim() ? "Give the page a name." : check.reason || "Choose an available address.");
    setSaving(true);
    setError("");
    const settings: any = { name: name.trim() };
    if (changed) settings.slug = cleanSlug;
    const response = await updatePage(page.id, { token, settings });
    setSaving(false);
    if (!response || response.error) return setError(response?.error || "Couldn't save. Try again.");
    setIsOpen(false);
    onSaved();
  };

  const fieldClass =
    "w-full rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-white/5 px-4 py-3 text-black dark:text-white focus:outline-none focus:border-black/30";

  return (
    <Modal title="Rename page" onClose={() => setIsOpen(false)} onSuccess={handleSave} successCaption={saving ? "Saving…" : "Save"}>
      <div className="flex flex-col gap-4 text-left">
        <div>
          <label className="block text-xs text-black/50 dark:text-white/50 mb-1.5">Page name</label>
          <input className={fieldClass} value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div>
          <label className="block text-xs text-black/50 dark:text-white/50 mb-1.5">Address</label>
          <div className={`${fieldClass} flex items-center`}>
            <span className="text-black/40 dark:text-white/40 whitespace-nowrap">{siteAddress ? `${siteAddress}/` : "/"}</span>
            <input
              className="w-full bg-transparent focus:outline-none"
              style={{ padding: 0, border: 0, marginLeft: 2 }}
              value={slug}
              onChange={(e) => setSlug(e.target.value.toLowerCase())}
            />
          </div>
          <div className="mt-2">
            {check.level ? (
              <Badge
                type="rounded"
                color={(check.level === "ok" ? "success" : check.level === "warn" ? "warning" : "error") as any}
                text={check.level === "warn" ? "Another page in this site uses this address" : check.reason || ""}
              />
            ) : (
              <span className="text-xs text-black/40">Checking…</span>
            )}
          </div>
        </div>
        {changed && liveSlug && (
          <Calout title={`This page is live at /${liveSlug}. The old address will stop working when you publish.`} />
        )}
        {error && <Calout title={error} />}
      </div>
    </Modal>
  );
};
